import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { AlertNotice } from '@/components/ui/alert-notice';
import OnboardingProjectHeader from './OnboardingProjectHeader';

type ScanStatus = 'idle' | 'scanning' | 'completed' | 'error';

interface ScanCardProps {
  org: string;
  project: string;
  repo: string;
  branch: string;
  scanBlocked?: boolean;
  scanBlockedMessage?: string;
  onStartScan: () => Promise<boolean>;
}

function ScanCard({
  org,
  project,
  repo,
  branch,
  scanBlocked = false,
  scanBlockedMessage,
  onStartScan,
}: ScanCardProps) {
  const [status, setStatus] = useState<ScanStatus>('idle');

  const handleStartScan = async () => {
    setStatus('scanning');
    try {
      const success = await onStartScan();
      setStatus(success ? 'completed' : 'error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div>
      <OnboardingProjectHeader
        org={org}
        project={project}
        repoName={repo}
        branchName={branch}
      />

      <AlertNotice
        message="A full repository code scan will be triggered. This builds the code inventory needed for coverage analysis."
        status="icon-information-outline"
        className="mb-5 w-full"
      />

      <div className="flex items-center justify-end gap-3 border-t border-[var(--inv-border-soft)] pt-3">
        <Button
          onClick={handleStartScan}
          disabled={
            scanBlocked || status === 'scanning' || status === 'completed'
          }
          className='hover:cursor-pointer'>
          {status === 'scanning' ? 'Scanning…' : 'Start Full Scan'}
        </Button>
        {status === 'scanning' && <Spinner compact />}
      </div>

      {scanBlocked && scanBlockedMessage && (
        <p className="mt-3 text-sm text-[var(--inv-text-secondary)]" role="status">
          {scanBlockedMessage}
        </p>
      )}

      {status === 'completed' && (
        <div className="mb-4 mt-3 rounded-[10px] border border-solid border-[#abefc6] bg-[#ecfdf3] px-3.5 py-2.5 text-sm text-[#027a48]">
          Scan completed successfully. Repository code inventory has been built.
        </div>
      )}

      {status === 'error' && (
        <div
          role="alert"
          className="mb-4 mt-3 rounded-[10px] border border-solid border-[#fecdca] bg-[#fef3f2] px-3.5 py-2.5 text-sm text-[#b42318]">
          Scan failed. Please try again.
        </div>
      )}
    </div>
  );
}

export default ScanCard;
